import {
  type KeyboardEventHandler,
  type MouseEventHandler,
  useCallback,
  useMemo,
} from 'react';
import { useProjectConnectedToLightroomQuery } from '@frameio/graphql-client/generated';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { getLink } from '@frameio/shared/routing';
import { consumeEvent } from '@frameio/utils';
import { Text, theme, Tooltip } from '@frameio/vapor';
import type { IconProps } from '@frameio/vapor-icons';
import { IconCloud, IconCloudLightroom } from '@frameio/vapor-icons';
import { useIsActiveItem, useOnItemSelect } from '../contexts';
import type { C2CConnectionsTreeItemProps, ProjectTreeItem } from '../types';
import TEXT from './generated/TEXT/ProjectTreeC2CConnections';
import {
  BaseContainer,
  HiddenLink,
  ICON_WIDTH,
  ItemContainer,
} from './TreeItem';

export const C2CConnections = ({
  id,
  isOpen,
  transition,
  type,
}: C2CConnectionsTreeItemProps) => {
  const item = useMemo<ProjectTreeItem>(() => ({ id, type }), [id, type]);

  const projectId = useActiveProjectId();
  const isActive = useIsActiveItem(item);
  const onItemSelect = useOnItemSelect();

  const { data } = useProjectConnectedToLightroomQuery({
    variables: { projectId },
    skip: !projectId,
  });
  const projectIsConnected = data?.account?.project?.lightroomConnection?.user;

  const href = useMemo(
    () =>
      getLink({
        page: 'c2cConnections',
        projectId,
      }),
    [projectId]
  );

  const isDisabled = transition === 'close';

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === 'Enter') {
      onItemSelect(item, href);
      consumeEvent(event);
    }
  };

  const handleClick: MouseEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (event.button === 2 || event.metaKey) return;

      onItemSelect(item, href);
    },
    [href, item, onItemSelect]
  );

  return (
    <BaseContainer
      key={id}
      $isActive={isActive}
      onClick={handleClick}
      onContextMenu={consumeEvent}
      onKeyDown={handleKeyDown}
    >
      <Tooltip disabled={!projectIsConnected} title={TEXT.connectedTooltip}>
        <ItemContainer
          key={id}
          expandable={false}
          id={id}
          isActive={isActive}
          isDisabled={isDisabled}
          isLink={true}
          isOpen={isOpen}
          name={TEXT.buttonLabel}
          paddingLeft={PADDING_LEFT}
          type={type}
        >
          {projectIsConnected ? (
            <IconCloudLightroom {...ICON_PROPS} />
          ) : (
            <IconCloud {...ICON_PROPS} />
          )}

          <Text
            color={isActive ? 'primary' : undefined}
            truncate={true}
            variant="body"
          >
            {TEXT.buttonLabel}
          </Text>

          {href && (
            <HiddenLink
              aria-label={TEXT.buttonLabel}
              href={href}
              tabIndex={-1}
            />
          )}
        </ItemContainer>
      </Tooltip>
    </BaseContainer>
  );
};

const PADDING_LEFT = 16;

const ICON_PROPS: IconProps = {
  color: theme.colors.icon.primary,
  size: ICON_WIDTH,
  style: { flexShrink: 0 },
};

TEXT.DEFINE({
  /** Label for the C2C Connections link in the ProjectTree */
  buttonLabel: 'C2C Connections',
  /** Tooltip shown when Lightroom is connected */
  connectedTooltip: 'Your project is connected to an Adobe Lightroom account.',
});
